import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Tag, Select, DatePicker, Input, Button,
  Row, Col, Statistic, Tooltip, Typography
} from 'antd';
import {
  SearchOutlined, ReloadOutlined, ClockCircleOutlined,
  WarningOutlined, UserOutlined, CheckCircleOutlined, CloseCircleOutlined
} from '@ant-design/icons';
import axiosInstance from '../../variables/api/axiosInstance';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import SideBar from '../../components/SideBar';
import NavBar from '../../components/NavBar';
import Footer from '../../components/Footer';

const ACTION_CONFIGS = {
  AUTH_LOGIN:           { label: 'Connexion',           color: 'green',    icon: '🔐', category: 'Auth' },
  AUTH_LOGOUT:          { label: 'Déconnexion',          color: 'default',  icon: '🚪', category: 'Auth' },
  AUTH_LOGIN_FAILED:    { label: 'Échec connexion',      color: 'red',      icon: '⛔', category: 'Auth' },
  AUTH_PASSWORD_CHANGE: { label: 'Changement mdp',       color: 'orange',   icon: '🔑', category: 'Auth' },
  AUTH_PASSWORD_RESET:  { label: 'Réinitialisation mdp', color: 'orange',   icon: '🔓', category: 'Auth' },
  CONTRACT_CREATE:      { label: 'Contrat créé',         color: 'blue',     icon: '📄', category: 'Contrats' },
  CONTRACT_UPDATE:      { label: 'Contrat modifié',      color: 'cyan',     icon: '✏️',  category: 'Contrats' },
  CONTRACT_DELETE:      { label: 'Contrat supprimé',     color: 'red',      icon: '🗑️',  category: 'Contrats' },
  CONTRACT_PRINT:       { label: 'Impression contrat',   color: 'purple',   icon: '🖨️',  category: 'Contrats' },
  CONTRACT_AVENANT:     { label: 'Avenant',              color: 'geekblue', icon: '📋', category: 'Contrats' },
  MIGRATION_IMPORT:     { label: 'Import migration',     color: 'gold',     icon: '📥', category: 'Migration' },
  USER_CREATE:          { label: 'Utilisateur créé',     color: 'blue',     icon: '👤', category: 'Utilisateurs' },
  USER_UPDATE:          { label: 'Utilisateur modifié',  color: 'cyan',     icon: '✏️',  category: 'Utilisateurs' },
  USER_DELETE:          { label: 'Utilisateur supprimé', color: 'red',      icon: '🗑️',  category: 'Utilisateurs' },
  USER_ACTIVATE:        { label: 'Activation compte',    color: 'green',    icon: '✅', category: 'Utilisateurs' },
  USER_DEACTIVATE:      { label: 'Désactivation compte', color: 'orange',   icon: '⏸️',  category: 'Utilisateurs' },
  PARAMS_CREATE:        { label: 'Paramètre créé',       color: 'volcano',  icon: '⚙️',  category: 'Paramétrage' },
  PARAMS_UPDATE:        { label: 'Paramètre modifié',    color: 'volcano',  icon: '⚙️',  category: 'Paramétrage' },
  PARAMS_DELETE:        { label: 'Paramètre supprimé',   color: 'red',      icon: '⚙️',  category: 'Paramétrage' },
  OTHER:                { label: 'Autre',                color: 'default',  icon: '•',  category: 'Autre' },
};

const ACTION_TYPE_OPTIONS = Object.entries(
  Object.entries(ACTION_CONFIGS).reduce((acc, [key, cfg]) => {
    if (!acc[cfg.category]) acc[cfg.category] = [];
    acc[cfg.category].push({ label: `${cfg.icon} ${cfg.label}`, value: key });
    return acc;
  }, {})
).map(([group, options]) => ({ label: group, options }));

const EMPTY_FILTERS = {
  actionTypes: [], utilisateurCodeId: null,
  dateRange: null, success: undefined, search: ''
};

const Logs = () => {
  const [logs,       setLogs]       = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [filters,    setFilters]    = useState(EMPTY_FILTERS);
  const [stats,      setStats]      = useState({ today: 0, failuresToday: 0, activeUsersToday: 0 });

  const fetchLogs = useCallback(async (overrides = {}) => {
    setLoading(true);
    try {
      const f = { ...filters, ...overrides };
      const p = new URLSearchParams();
      p.set('page', String(Math.max(0, (overrides.current || pagination.current) - 1)));
      p.set('size', String(pagination.pageSize));
      (f.actionTypes || []).forEach(t => p.append('actionTypes', t));
      if (f.utilisateurCodeId) p.set('utilisateurCodeId', f.utilisateurCodeId);
      if (f.dateRange?.[0])    p.set('from', dayjs(f.dateRange[0]).toISOString());
      if (f.dateRange?.[1])    p.set('to',   dayjs(f.dateRange[1]).toISOString());
      if (f.success !== undefined && f.success !== null) p.set('success', String(f.success));
      if (f.search)            p.set('search', f.search);

      const { data } = await axiosInstance.get(`/api/activity-logs?${p.toString()}`);
      setLogs(data.content || []);
      setPagination(prev => ({
        ...prev,
        total:   data.totalElements || 0,
        current: overrides.current || prev.current,
      }));
    } catch {
      toast.error('Erreur lors du chargement des journaux');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.current, pagination.pageSize]); // eslint-disable-line

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get('/api/activity-logs/stats');
      setStats(data);
    } catch { /* silencieux */ }
  }, []);

  useEffect(() => { fetchLogs(); fetchStats(); }, []); // eslint-disable-line

  const handleFilter = (patch) => {
    const next = { ...filters, ...patch };
    setFilters(next);
    fetchLogs({ ...patch, current: 1 });
  };

  const resetFilters = () => {
    setFilters(EMPTY_FILTERS);
    fetchLogs({ ...EMPTY_FILTERS, current: 1 });
  };

  const columns = [
    {
      title: 'Date / Heure', dataIndex: 'createdAt', key: 'ts', width: 160,
      render: v => (
        <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#555' }}>
          {v ? dayjs(v).format('DD/MM/YY HH:mm:ss') : '—'}
        </span>
      ),
    },
    {
      title: 'Utilisateur', key: 'user', width: 175,
      render: (_, r) => r.username ? (
        <div>
          <div style={{ fontWeight: 600, fontSize: 13, color: '#242166' }}>
            {r.nomComplet || r.username}
          </div>
          <div style={{ color: '#aaa', fontSize: 11 }}>@{r.username}</div>
        </div>
      ) : <span style={{ color: '#ddd' }}>—</span>,
    },
    {
      title: 'Action', dataIndex: 'actionType', key: 'action', width: 195,
      render: type => {
        const cfg = ACTION_CONFIGS[type] || ACTION_CONFIGS.OTHER;
        return (
          <Tag color={cfg.color} style={{ borderRadius: 20, fontWeight: 500 }}>
            {cfg.icon} {cfg.label}
          </Tag>
        );
      },
    },
    {
      title: 'Entité', key: 'entity', width: 185,
      render: (_, r) => r.entityType ? (
        <div>
          <Tag style={{ fontSize: 11, borderRadius: 4, marginBottom: 2 }}>{r.entityType}</Tag>
          {r.entityCodeId && (
            <div style={{ fontSize: 11, color: '#666' }}>{r.entityCodeId}</div>
          )}
        </div>
      ) : <span style={{ color: '#ddd' }}>—</span>,
    },
    {
      title: 'Description', dataIndex: 'description', key: 'desc', ellipsis: true,
      render: v => v ? (
        <Tooltip title={v}><span style={{ fontSize: 13 }}>{v}</span></Tooltip>
      ) : '—',
    },
    {
      title: 'IP', dataIndex: 'ipAddress', key: 'ip', width: 125,
      render: v => v ? (
        <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#888' }}>{v}</span>
      ) : '—',
    },
    {
      title: '', dataIndex: 'success', key: 'ok', width: 44, align: 'center',
      render: v => v
        ? <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 16 }} />
        : <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 16 }} />,
    },
  ];

  const cardStyle = { borderRadius: 12, border: '1px solid #E6F0FF' };

  return (
    <div className="layout-wrapper layout-content-navbar">
      <div className="layout-container">
        <SideBar currentMenu="LOGS" />
        <div className="layout-page">
          <NavBar />
          <div className="content-wrapper">
            <div className="container-xxl flex-grow-1 container-p-y">
              <div style={{ minHeight: '100vh', background: '#F7F9FB', padding: '12px 30px' }}>

                {/* En-tête */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
                  <div>
                    <Typography.Title level={2} style={{ color: '#242166', margin: 0 }}>
                      Journaux d'activité
                    </Typography.Title>
                    <Typography.Text type="secondary">
                      Traçabilité complète des actions dans le système
                    </Typography.Text>
                  </div>
                  <Button icon={<ReloadOutlined />} onClick={() => { fetchLogs(); fetchStats(); }}>
                    Actualiser
                  </Button>
                </div>

                {/* Statistiques */}
                <Row gutter={16} style={{ marginBottom: 20 }}>
                  {[
                    {
                      title: "Actions aujourd'hui",
                      value: stats.today,
                      icon: <ClockCircleOutlined />,
                      color: '#242166',
                      border: '#E6F0FF',
                    },
                    {
                      title: "Échecs aujourd'hui",
                      value: stats.failuresToday,
                      icon: <WarningOutlined />,
                      color: stats.failuresToday > 0 ? '#ff4d4f' : '#52c41a',
                      border: '#FFE6E6',
                    },
                    {
                      title: "Utilisateurs actifs aujourd'hui",
                      value: stats.activeUsersToday,
                      icon: <UserOutlined />,
                      color: '#52c41a',
                      border: '#E6FFE6',
                    },
                  ].map((s, i) => (
                    <Col span={8} key={i}>
                      <Card style={{ ...cardStyle, border: `1px solid ${s.border}` }}>
                        <Statistic
                          title={s.title}
                          value={s.value}
                          prefix={React.cloneElement(s.icon, { style: { color: s.color } })}
                          valueStyle={{ color: s.color, fontWeight: 700 }}
                        />
                      </Card>
                    </Col>
                  ))}
                </Row>

                {/* Filtres */}
                <Card style={{ ...cardStyle, marginBottom: 16 }}>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Input.Search
                      placeholder="Rechercher (utilisateur, description, entité...)"
                      style={{ width: 290 }}
                      allowClear
                      prefix={<SearchOutlined />}
                      onSearch={v => handleFilter({ search: v, current: 1 })}
                      onChange={e => { if (!e.target.value) handleFilter({ search: '', current: 1 }); }}
                    />
                    <Select
                      mode="multiple"
                      placeholder="Types d'action"
                      style={{ minWidth: 250 }}
                      options={ACTION_TYPE_OPTIONS}
                      allowClear
                      maxTagCount={2}
                      onChange={v => handleFilter({ actionTypes: v, current: 1 })}
                    />
                    <DatePicker.RangePicker
                      showTime
                      format="DD/MM/YY HH:mm"
                      placeholder={['Du', 'Au']}
                      onChange={dates => handleFilter({ dateRange: dates, current: 1 })}
                    />
                    <Select
                      placeholder="Statut"
                      style={{ width: 120 }}
                      allowClear
                      options={[
                        { label: '✅ Succès', value: true },
                        { label: '❌ Échec',  value: false },
                      ]}
                      onChange={v => handleFilter({ success: v, current: 1 })}
                    />
                    <Button onClick={resetFilters}>Réinitialiser</Button>
                  </div>
                </Card>

                {/* Tableau */}
                <Card style={cardStyle}>
                  <Table
                    columns={columns}
                    dataSource={logs}
                    loading={loading}
                    rowKey="id"
                    size="small"
                    pagination={{
                      current:         pagination.current,
                      pageSize:        pagination.pageSize,
                      total:           pagination.total,
                      showSizeChanger: true,
                      pageSizeOptions: ['10', '20', '50', '100'],
                      showTotal: (total, range) =>
                        `${range[0]}-${range[1]} sur ${total} entrées`,
                    }}
                    onChange={p => {
                      setPagination(prev => ({ ...prev, current: p.current, pageSize: p.pageSize }));
                      fetchLogs({ current: p.current });
                    }}
                    rowClassName={r => !r.success ? 'log-row-error' : ''}
                  />
                </Card>

                <style>{`
                  .log-row-error td              { background: #fff2f0 !important; }
                  .log-row-error:hover td        { background: #ffe4e1 !important; }
                `}</style>
              </div>
            </div>
            <Footer />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Logs;
